import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from '@shared/models';

interface Token {
    hash: string,
    expiresIn: number,
    refresh: string,
    refreshExpiresIn: number,
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    authenticate = new Subject<any>();

    constructor(
        private http: HttpClient,
        private router: Router,
    ) { }

    hasAuthentication(): Observable<boolean> {
        return this.authenticate.asObservable();
    }

    logout() {
        localStorage.removeItem('ArMtoken');
        localStorage.removeItem('ArMuser');
        this.authenticate.next(false);
        this.router.navigate(['/login']);
    }

    loginForm(form: { username: string, password: string }): Promise<any> {
        const formDataEncode = new URLSearchParams();
        formDataEncode.append('client_id', 'ARIEL_MANAGER');
        formDataEncode.append('grant_type', 'password');
        formDataEncode.append('scope', 'openid');
        formDataEncode.append('username', form.username);
        formDataEncode.append('password', form.password);

        return this.login(formDataEncode);
    }

    loginRefreshToken(): Promise<any> {
        const formDataEncode = new URLSearchParams();
        formDataEncode.append('client_id', 'ARIEL_MANAGER');
        formDataEncode.append('grant_type', 'refresh_token');
        formDataEncode.append('refresh_token', this.token.refresh);
        return this.login(formDataEncode);
    }

    private login(formDataEncode: URLSearchParams) {
        const headers: any = { 'Content-Type': 'application/x-www-form-urlencoded' };
        return new Promise((resolve, reject) => {
            this.http.post(`${environment.keycloakURL}/protocol/openid-connect/token`, formDataEncode.toString(), { headers }).subscribe({
                next: (res: any) => {
                    const t = new Date();
                    this.token = {
                        hash: res.access_token,
                        expiresIn: t.setSeconds(t.getSeconds() + res.expires_in),
                        refresh: res.refresh_token,
                        refreshExpiresIn: t.setSeconds(t.getSeconds() + res.refresh_expires_in)
                    };

                    this.http.get<User>(`${environment.keycloakURL}/protocol/openid-connect/userinfo`).subscribe({
                        next: (res) => {
                            const { sub, email, name, roles } = res;
                            this.user = { sub, email, name, roles };
                            this.authenticate.next(true);
                            resolve(this.user);
                        }, error: () => reject(false)
                    });
                }, error: (err) => reject(err)
            });
        });
    }

    recoverPassword(email: string) {
        return this.http.post(`${environment.managerURL}/users/forgot-password`, { email });
    }

    resetPassword(id: string) {
        return this.http.put(`${environment.managerURL}/users/reset-password`, { id });
    }

    set token(token) {
        localStorage.setItem('ArMtoken', JSON.stringify(token));
    }

    get token(): Token {
        const token = localStorage.getItem('ArMtoken');
        return token ? JSON.parse(token) : undefined as any;
    }

    set user(user) {
        localStorage.setItem('ArMuser', JSON.stringify(user));
    }

    get user(): User {
        const user = localStorage.getItem('ArMuser');
        return user ? JSON.parse(user) : undefined as any;
    }
}
